import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchVoiceToolPayloads, requestVoiceSession, startVoiceConversation,
} from "@/lib/voice";
import { anonymousKeyFor } from "@/lib/dany";
import { attachPhotoToCall } from "@/lib/danyPhotos";

/**
 * Voice mode for the Dany thread screen (DY1.6).
 *
 * Holds the whole lifecycle — permission, connection, live transcript, the
 * settle back into the persisted thread — so the screen only has to render a
 * state and a list of turns.
 *
 * **The live transcript and the stored one are two different things, on
 * purpose.** What arrives over the WebSocket is what is being said right now,
 * and it has to appear immediately or the caller cannot tell whether they were
 * heard. What ends up in `dany_messages` arrives seconds later, through
 * ElevenLabs' post-call webhook, and is the authoritative version. So the live
 * turns are rendered as they come, and once the call is over the screen refetches
 * the thread and retires each live turn as the stored transcript comes to carry
 * it — turn by turn, never all at once, so the tail the webhook has not written
 * yet stays on screen instead of blinking out (see `landedInStored`).
 *
 * That refetch is also the only honest way to show the drop case. A connection
 * that dies mid-sentence still produces a post-call delivery, so the transcript
 * is not lost — but the browser cannot know that from its own state. Asking the
 * server is the difference between "your words are saved" as a claim and as an
 * observation.
 */

/** When to look for the persisted transcript after a call ends, in ms. */
export const SETTLE_DELAYS = [5000, 15000, 35000];

/**
 * A second look, when the first one comes back with nothing.
 *
 * **Measured before it was changed, which is why it stayed small.** The
 * emission-to-mount path is three legs, and production numbers for all three
 * exist: ElevenLabs timed our own tool endpoint at 0.169-0.207s across the five
 * calls of conversation conv_7901m0h4ev9vf96a4f1qvrst0p50; it emits
 * `agent_tool_response` inside the same second it made the call; and the
 * payload fetch measured 0.10-0.14s from a real client. The card is on screen
 * roughly a third of a second after the tool ran, and Dany does not speak about
 * it for another two — so the transport is ahead of the conversation, and
 * polling it faster would buy nothing anyone can perceive.
 *
 * What is worth guarding is the *one-shot*. `fetchVoiceToolPayloads` turns
 * every failure into an empty list on purpose — an exception on this path would
 * end a call — so a dropped request and a tool that produced nothing look
 * identical, and the first was permanent: one lost fetch meant the card never
 * arrived at all. One retry costs a single request in the rare case and is the
 * whole difference between a blip and a missing card.
 */
const WIDGET_RETRY_MS = 700;

/** Case and spacing are not identity — the two transcripts are the same words. */
function normalise(text) {
  return String(text || "").replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Has this live turn already landed in the stored transcript?
 *
 * The question the closing swap has to ask about each turn separately. The
 * webhook writes the whole conversation in one go, but it writes it *seconds*
 * after the hangup, and the screen refetches three times — so between the first
 * attempt and the last there is a stored transcript that is real and
 * incomplete. Retiring a live turn before its row exists is what made the last
 * thing said blink out; keeping one after its row exists would show it twice.
 *
 * Two kinds of identity, because the two channels record two different things:
 *
 *   * **Widget turns match on the tool and its place in the thread**, because
 *     the ids do NOT survive the webhook: the stash the live cards were
 *     fetched from is deleted and rewritten. See the branch itself.
 *   * **Spoken turns match on their words**, because the live turn and the
 *     stored row have no id in common: the live one is numbered by arrival.
 *     Dany's stored turns are FOLDED — `turnsFromTranscript` joins the
 *     assistant rows of one turn with blank lines — so a spoken line is
 *     contained by its stored turn rather than equal to it. The caller's rows
 *     are one-to-one, and containment is tested both ways there so that a
 *     difference in the punctuation ElevenLabs restores does not read as a
 *     different sentence and leave a duplicate on screen.
 */
export function landedInStored(turn, stored = []) {
  if (!turn.text && turn.widgets?.length) {
    // **Not by id, because the ids do not survive the webhook** (T11 N38).
    // `_stashed_tool_payloads` DELETES the pending rows the live cards were
    // fetched from and the webhook re-creates them in conversation order with
    // fresh `message_id`s, so this branch could never be true and every card a
    // call produced stayed on screen next to the row that replaced it. It went
    // unseen because `dropSuperseded` hid the duplicate for the tools that
    // happened to have a supersession rule, and only for those.
    //
    // The honest key is the tool plus the position: the rewritten row is the
    // same card, written LATER, so it carries a higher `seq` than the pending
    // row did. A card from an earlier call in the same thread sits at a lower
    // one and cannot retire this call's, which is the mistake a match on tool
    // name alone would make.
    const landed = [];
    stored.forEach((s) => (s.widgets || []).forEach((w) => landed.push(w)));
    return turn.widgets.every((w) => landed.some(
      (s) => s.tool === w.tool
        && (typeof w.seq !== "number" || typeof s.seq !== "number"
            ? s.id === w.id
            : s.seq >= w.seq)
    ));
  }
  // T19 F5. A photo attached during the call has landed when a stored user
  // turn carries it — it has no words to match on.
  if (turn.kind === "user" && turn.photos?.length) {
    return stored.some((s) => s.kind === "user"
      && (s.photos || []).some((url) => turn.photos.includes(url)));
  }
  const spoken = normalise(turn.text);
  // An empty turn carries nothing that can go missing.
  if (!spoken) return true;
  return stored.some((s) => {
    if (s.kind !== turn.kind) return false;
    const text = normalise(s.text);
    if (!text) return false;
    return turn.kind === "user"
      ? text === spoken || text.includes(spoken) || spoken.includes(text)
      : text.includes(spoken);
  });
}

/**
 * How long before the agent's own hangup the surface starts warning.
 *
 * The countdown is a MIRROR, never the mechanism: ElevenLabs ends the call from
 * `turn.silence_end_call_timeout`, and this only decides when the ring starts
 * emptying. Keeping the two apart is what makes a frozen tab still stop billing
 * — a browser timer is precisely the thing that is not running in that case.
 */
const ENDING_WARNING_MS = 5000;
/** Fallback when the session response predates the setting. */
const DEFAULT_IDLE_TIMEOUT_MS = 10000;

/** Nothing stored yet. Hoisted so the reconcile's identity does not churn. */
const EMPTY_IDS = new Set();

export function useVoiceMode({
  threadId, language, onThreadReady, onTranscriptSettled, storedWidgetIds,
}) {
  // Read only to re-check the session when the backend says it has died (C-16).
  const { refresh: refreshAuth } = useAuth();
  // The state machine the surface renders (DY1.6-UX):
  //
  //   idle        no call
  //   connecting  permission granted, socket opening
  //   listening   the caller's turn — microphone open
  //   thinking    they stopped talking; Dany has not started
  //   speaking    Dany is producing audio
  //   ending      the inactivity timeout is about to fire
  //
  // Every transition comes from something already on the socket or from the
  // microphone we are already reading. No extra calls, and nothing polls.
  const [state, setState] = useState("idle");
  // The tool Dany is running right now, for contextual copy. "" when none.
  const [tool, setTool] = useState("");
  const [error, setError] = useState(null);
  const [remainingMinutes, setRemainingMinutes] = useState(null);
  // Turns spoken during THIS call, newest last. Cleared when the stored
  // transcript takes over.
  const [liveTurns, setLiveTurns] = useState([]);

  const conversation = useRef(null);
  const timers = useRef([]);
  const active = useRef(false);
  // The inactivity mirror: one timer to enter "ending", one to stop pretending
  // if the socket has not closed by then.
  const idleTimers = useRef([]);
  const idleMs = useRef(DEFAULT_IDLE_TIMEOUT_MS);
  // Dany is mid-utterance. The countdown must not run while she is talking —
  // silence from the caller during Dany's own turn is not inactivity.
  const danySpeaking = useRef(false);
  // Widget rows currently placed on screen, by message id. The endpoint
  // answers with the thread's whole widget state, not a delta, so this is what
  // turns that answer into "what changed" in both directions — a row that
  // appeared, and a row that is gone.
  const shownTools = useRef(new Set());
  // One in-flight payload fetch at a time. The watch and the response event can
  // both ask at once, and two answers carrying the same fresh row would each
  // pass the `shownTools` filter before either had added to it — the same card
  // twice, from a guard that reads correct.
  const collecting = useRef(false);
  // When ElevenLabs told us a tool was starting, so the card's arrival can be
  // measured against it. A ref because the socket handler is one closure over
  // the whole call.
  const toolRequestedAt = useRef(null);
  const widgetRetry = useRef(null);
  // The conversation the last call was for, so a reconcile can be asked for
  // from outside the socket's closure — by a widget that has just committed,
  // which is the other thing that changes what belongs on screen.
  const voiceThread = useRef(null);
  // The grant of the call in progress (T19): a photo attached mid-call is
  // filed against it, which is what the agent's `get_attachments` reads.
  const voiceGrant = useRef(null);

  /**
   * Bring the cards on screen into line with the thread, now.
   *
   * The call is what the transcript is FOR — "I found three plumbers" is worth
   * nothing if the plumbers show up after hanging up and reloading. The socket
   * event says a tool finished; the payload behind it is fetched, because
   * ElevenLabs never had it (see `fetchVoiceToolPayloads`).
   *
   * **It reconciles rather than appends, and that is the whole of T11 N38 /
   * N40 / N41 / N47.** It used to add whatever the endpoint had not shown it
   * yet, which made this a feed of new cards and left the screen with no way to
   * express two of the three things a thread does. A card could never change
   * and a card could never leave, so a corrected quote stacked a second Send
   * button under the first instead of replacing it, and a receipt written by
   * `POST /dany/actions/{id}/execute` — which marks nothing `pending` — could
   * not arrive at all until the component re-mounted and re-read the
   * transcript. Production, dthr_c8b2d5779126: three live `send_quote`
   * proposals at once, at nothing, $500 and $600.
   *
   * So the endpoint now answers with the thread's whole widget state and this
   * is a diff against it: rows that are new get placed, rows that are gone get
   * removed, and rows already placed are left alone. Supersession is still the
   * page's to decide (`dropSuperseded`) — a superseded proposal is a row that
   * still exists, and hiding it is a statement about conversation order rather
   * than about what the server holds.
   *
   * `storedWidgetIds` keeps the two readings from doubling: everything the page
   * already has from the stored transcript is not a live card.
   *
   * New cards are appended as their own Dany turn rather than merged into the
   * last one: the tool completes BEFORE she says what she found, so at this
   * instant the last turn is the caller's question. Appending puts the cards
   * between the question and the spoken answer, which is where the text channel
   * puts them and where a reader expects them.
   */
  const reconcileWidgets = useCallback(async (voiceThreadId) => {
    if (!voiceThreadId) return false;
    if (collecting.current) return false;
    collecting.current = true;
    let rows;
    try {
      rows = await fetchVoiceToolPayloads(voiceThreadId);
    } finally {
      collecting.current = false;
    }
    const stored = storedWidgetIds?.() || EMPTY_IDS;
    const live = rows.filter(
      (row) => row.message_id && !stored.has(row.message_id)
    );
    const byId = new Set(live.map((row) => row.message_id));
    const fresh = live.filter((row) => !shownTools.current.has(row.message_id));
    const gone = [...shownTools.current].filter((id) => !byId.has(id));
    if (!fresh.length && !gone.length) return false;
    gone.forEach((id) => shownTools.current.delete(id));
    fresh.forEach((row) => shownTools.current.add(row.message_id));
    setLiveTurns((prev) => {
      let next = prev;
      if (gone.length) {
        const dead = new Set(gone);
        next = next
          .map((turn) => {
            if (turn.text || !turn.widgets?.length) return turn;
            const widgets = turn.widgets.filter((w) => !dead.has(w.id));
            return widgets.length === turn.widgets.length
              ? turn : { ...turn, widgets };
          })
          // A turn emptied by this was a card and nothing else. A spoken turn
          // always has text, so nothing else can match.
          .filter((turn) => turn.text || turn.widgets?.length);
      }
      if (fresh.length) {
        next = [...next, {
          kind: "dany",
          text: "",
          widgets: fresh.map((row) => ({
            tool: row.tool_name, data: row.data,
            id: row.message_id, seq: row.seq,
          })),
          id: `voice-tool-${fresh[0].message_id}`,
        }];
      }
      return next;
    });
    return fresh.length > 0;
  }, [storedWidgetIds]);

  /**
   * Fetch this tool's cards, and say how long they took to appear.
   *
   * The stamp comes from the tool REQUEST event — the earliest moment the
   * browser knows a tool is running — and the log is written when the cards are
   * committed to the screen, so the number is emission-to-mount as a caller
   * experiences it rather than a round trip in isolation. It is the only way
   * this stays measured: all three legs are outside this file, and the console
   * line is what would show a regression in any of them.
   */
  const collectAndReport = useCallback(async (voiceThreadId) => {
    const report = () => {
      const at = toolRequestedAt.current;
      if (at == null) return;
      console.info(
        "[FixyDany voice] widget emitted -> mounted in %dms",
        Math.round((window.performance?.now?.() ?? 0) - at)
      );
      toolRequestedAt.current = null;
    };
    if (await reconcileWidgets(voiceThreadId)) return report();
    // Nothing fresh. Either the tool produced no payload — denied, failed, both
    // by design — or the request was lost, and those are indistinguishable from
    // here. One more look settles it cheaply.
    widgetRetry.current = window.setTimeout(async () => {
      widgetRetry.current = null;
      if (!active.current) return;
      if (await reconcileWidgets(voiceThreadId)) report();
    }, WIDGET_RETRY_MS);
    return undefined;
  }, [reconcileWidgets]);

  const clearWidgetRetry = useCallback(() => {
    if (widgetRetry.current) window.clearTimeout(widgetRetry.current);
    widgetRetry.current = null;
  }, []);

  const clearTimers = useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  }, []);

  const clearIdle = useCallback(() => {
    idleTimers.current.forEach((id) => window.clearTimeout(id));
    idleTimers.current = [];
  }, []);

  /**
   * Restart the inactivity mirror. Called whenever the caller is demonstrably
   * present: they made a sound, or Dany just finished a turn and it is their
   * go again.
   */
  const armIdle = useCallback(() => {
    clearIdle();
    const lead = Math.max(0, idleMs.current - ENDING_WARNING_MS);
    idleTimers.current.push(window.setTimeout(() => {
      // Only warn if nothing else is happening. Dany talking, or a tool
      // running, is not the caller having wandered off.
      setState((current) => (current === "listening" ? "ending" : current));
    }, lead));
  }, [clearIdle]);

  useEffect(() => () => {
    active.current = false;
    clearTimers();
    clearIdle();
    clearWidgetRetry();
    conversation.current?.stop();
  }, [clearTimers, clearIdle, clearWidgetRetry]);

  const stop = useCallback(() => {
    clearWidgetRetry();
    conversation.current?.stop();
    conversation.current = null;
  }, [clearWidgetRetry]);

  const start = useCallback(async ({ turnstileToken } = {}) => {
    if (conversation.current) return;
    setError(null);
    setLiveTurns([]);
    // A new call shows its own cards. The ids are per-row and never repeat, but
    // leaving the set to grow across calls would be a leak with no upper bound.
    shownTools.current.clear();
    clearWidgetRetry();
    toolRequestedAt.current = null;
    setState("connecting");
    active.current = true;

    let session;
    try {
      session = await requestVoiceSession({ threadId, language, turnstileToken });
    } catch (err) {
      setState("idle");
      setError({ code: err.code || "voice_error", message: err.message,
                 allowanceMinutes: err.allowanceMinutes });
      // The backend refuses a session cookie that no longer resolves and clears
      // it (C-16). The page still believes it is signed in; re-reading /auth/me
      // is what makes the screen agree with the server.
      if (err.code === "voice_sign_in_required") refreshAuth?.();
      return;
    }
    if (!active.current) return;
    // **The screen has to be told which conversation this is, immediately.**
    // `POST /voice/session` mints a thread when the caller has none, and until
    // 2026-08-20 that id lived only inside this hook: the page kept `threadId`
    // null for the whole call. Two things broke from that one gap, and both
    // were reported as separate bugs.
    //
    // The confirmation card is handed the page's `threadId` so it can look up
    // the anonymous key that owns the proposal. With null it found no key, so
    // `POST /dany/actions/{id}/execute` arrived with neither cookie nor key and
    // was answered `action_not_found` — where a key would have earned
    // `registration_required` and swapped the card for the sign-up form.
    // Production, dthr_f890ba159d66 at 03:08: *"I clicked on the Publish this
    // job button, but it just won't publish anything or it won't prompt me for
    // creating my account."* The same action id executed fine three minutes
    // later, after a reload put the id in the URL.
    //
    // And a reload during a call landed on an empty composer, because the URL
    // still said `/dany` — the conversation was not lost, it was unaddressed.
    onThreadReady?.(session.thread_id);
    setRemainingMinutes(
      typeof session.remaining_minutes === "number" ? session.remaining_minutes : null
    );
    idleMs.current = (Number(session.idle_timeout_seconds) || 0) * 1000
      || DEFAULT_IDLE_TIMEOUT_MS;

    const settledThreadId = session.thread_id;
    voiceThread.current = settledThreadId;
    voiceGrant.current = session.grant || null;

    conversation.current = startVoiceConversation({
      session,
      onEvent: (event, data) => {
        switch (event) {
          case "speech":
            // The caller started or stopped making sound. Starting rescues a
            // countdown in progress without them having to do anything but
            // talk; stopping is the trigger for "thinking", which is why it is
            // measured here rather than waited for from the socket.
            if (data.active) {
              danySpeaking.current = false;
              clearIdle();
              setState((current) => (current === "speaking" ? current : "listening"));
            } else {
              setState((current) => (
                current === "listening" || current === "ending" ? "thinking" : current
              ));
              armIdle();
            }
            break;
          case "tool":
            setTool(data.running ? String(data.name || "") : "");
            if (data.running) {
              setState((current) => (current === "speaking" ? current : "thinking"));
              // The clock for the card starts here, not at the response event:
              // this is the first moment the browser knows a tool is running,
              // and it is what "emission" means from the caller's side.
              toolRequestedAt.current = window.performance?.now?.() ?? 0;
            } else {
              // Finished. The row `run_tool` stashed is committed by now — it
              // is written before ElevenLabs is answered, and this event comes
              // after that answer — so the cards can simply be read.
              clearWidgetRetry();
              collectAndReport(settledThreadId);
            }
            break;
          case "status":
            if (data.state === "ended") {
              conversation.current = null;
              clearIdle();
              clearWidgetRetry();
              setTool("");
              setState("idle");
              // Give the post-call webhook time to land, then let the screen
              // read the stored transcript. Several attempts because the
              // delivery is not instant and a long call takes longer to
              // transcribe than a short one.
              clearTimers();
              SETTLE_DELAYS.forEach((delay) => {
                timers.current.push(window.setTimeout(() => {
                  // Retire the live turns the stored transcript now carries and
                  // keep the rest. Never `setLiveTurns([])`: the screen renders
                  // stored-then-live, so emptying this before the tail has been
                  // written is a hole in the conversation for as long as the
                  // next attempt takes. Idempotent, so the later attempts cost
                  // nothing once everything has landed.
                  onTranscriptSettled?.(settledThreadId, (stored) => setLiveTurns(
                    (prev) => {
                      const kept = prev.filter((t) => !landedInStored(t, stored));
                      return kept.length === prev.length ? prev : kept;
                    }
                  ));
                }, delay));
              });
            } else if (data.state === "speaking") {
              danySpeaking.current = true;
              clearIdle();
              setTool("");
              setState("speaking");
            } else if (data.state === "listening") {
              // Dany finished her turn. The caller's silence starts counting
              // from here, which is exactly the definition in the brief.
              if (danySpeaking.current) {
                danySpeaking.current = false;
                armIdle();
              }
              setState((current) => (current === "ending" ? current : "listening"));
            } else {
              setState(data.state);
            }
            break;
          case "user":
            if (data.text?.trim()) {
              setLiveTurns((prev) => [
                ...prev,
                { kind: "user", text: data.text, id: `voice-user-${prev.length}` },
              ]);
            }
            break;
          case "agent":
            if (data.text?.trim()) {
              setLiveTurns((prev) => [
                ...prev,
                { kind: "dany", text: data.text, widgets: [],
                  id: `voice-dany-${prev.length}` },
              ]);
            }
            break;
          case "agentCorrection":
            // Dany revised what she just said, because the caller interrupted
            // partway through it. The screen shows the corrected line rather
            // than both — the words after the interruption were never heard.
            setLiveTurns((prev) => {
              if (!prev.length || !data.text?.trim()) return prev;
              const next = [...prev];
              for (let i = next.length - 1; i >= 0; i -= 1) {
                if (next[i].kind === "dany") {
                  next[i] = { ...next[i], text: data.text };
                  break;
                }
              }
              return next;
            });
            break;
          case "error":
            setError({ code: data.code, message: data.message });
            break;
          default:
            break;
        }
      },
    });
  }, [threadId, language, clearTimers, clearIdle, armIdle, onThreadReady,
      onTranscriptSettled, collectAndReport, clearWidgetRetry, refreshAuth]);

  /**
   * Re-read the thread's cards, because something outside the socket changed
   * them.
   *
   * The other half of T11 N38: until now nothing a widget did could reach the
   * thread it was drawn in. `DanyWidget` is handed `tool`, `data`,
   * `onAskCategory` and `threadId`, and that is all — so a Publish or a Send
   * or a Discard changed the card's own local state and left the conversation
   * around it describing a world that no longer existed. In the text channel
   * the next turn re-read the transcript and hid it; on a call there is no
   * next turn to re-read anything, which is why this only ever looked like a
   * voice bug.
   *
   * Deliberately the same reconcile the socket uses, not a second path. One
   * function, three callers.
   */
  const syncWidgets = useCallback(
    () => reconcileWidgets(voiceThread.current), [reconcileWidgets]);

  /**
   * T19 F5. A photo picked during the call goes INTO the call (Plan A of X6).
   *
   * The server reads it with the same diagnosis as a typed photo and writes it
   * into the thread (`POST /voice/threads/{id}/photo`); this puts its
   * thumbnail on the caller's side of the live transcript, where they attached
   * it, hands the reading and the caller's line to the open socket, and pulls
   * the cards — a live draft comes back carrying the photo.
   */
  const attachToCall = useCallback(async (photo) => {
    const id = voiceThread.current;
    if (!id || !conversation.current) return;
    const data = await attachPhotoToCall(id, photo.file_id, {
      anonymousKey: anonymousKeyFor(id),
      grant: voiceGrant.current,
    });
    setLiveTurns((prev) => [
      ...prev,
      { kind: "user", text: "", photos: [photo.url], id: `voice-photo-${prev.length}` },
    ]);
    conversation.current?.sendContext?.(data.context_id || "fixy_photo", data.context);
    conversation.current?.sendUserMessage?.(data.nudge);
    reconcileWidgets(id);
  }, [reconcileWidgets]);

  return {
    attachToCall,
    syncVoiceWidgets: syncWidgets,
    voiceState: state,
    voiceTool: tool,
    voiceEndingMs: ENDING_WARNING_MS,
    voiceActive: state !== "idle",
    voiceError: error,
    clearVoiceError: () => setError(null),
    remainingMinutes,
    liveTurns,
    startVoice: start,
    stopVoice: stop,
  };
}
